import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState, } from 'react';
import { StyleSheet, View } from 'react-native';
import Animated, { runOnJS, useAnimatedRef, useAnimatedScrollHandler, useDerivedValue, useSharedValue, } from 'react-native-reanimated';
import { AppView } from 'design-system/AppView';
import NotificationsListScreenHeader from 'features/notifications/components/NotificationsListScreenHeader';
import { selectSelectedSpace, selectSpaces, } from 'features/spaces/reducers/selectors';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { triggerSelectorPress } from 'utils/hapticFeedback';
import useColors from 'hooks/useColors';
import { IconSettings } from 'design-system/icons/IconSettings';
import NavigationHeaderRightIconButton from 'design-system/NavigationHeaderRightIconButton';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { selectSpaceUnreadNotifications } from 'features/feed/selectors';
import { NotificationsWalkthrough } from 'features/feed/components/walkthrough/notification';
import { StatusBar } from 'design-system';
import { setShowNotificationsWalkThrough, setHasSeenNotificationsWalkThrough, } from 'actions/walkthrough';
import { useDisableAndroidBackButton } from 'hooks';
import { isWeb } from '../../../constants';
import { NotificationContainer } from '../components/NotificationContainer';
import { resetExternalUnreadNotifications } from '../actions';
import useNotificationWalkthroughLayout from '../hooks/useNotificationWalkthroughLayout';
export const NotificationsListScreen = ({ navigation, }) => {
    const colors = useColors();
    const spaces = useAppSelector(selectSpaces);
    const selectedSpaceId = useAppSelector(selectSelectedSpace);
    const unreadCounts = useAppSelector(selectSpaceUnreadNotifications);
    const { contentWidth: screenWidth } = useAppFrameDimensions(isWeb);
    const [displayCounts, setDisplayCounts] = useState(unreadCounts || {});
    const [prevSpaceIndex, setPrevSpaceIndex] = useState(0);
    const dispatch = useAppDispatch();
    const { onLayout: onNotificationLayout, notificationPosition } = useNotificationWalkthroughLayout();
    useEffect(() => {
        if (unreadCounts) {
            setDisplayCounts(unreadCounts);
        }
    }, [unreadCounts]);
    const resetUnreadCount = useCallback((spaceId) => {
        if (unreadCounts && unreadCounts[spaceId]) {
            dispatch(resetExternalUnreadNotifications(spaceId));
        }
    }, [unreadCounts]);
    const reorderedSpaces = useMemo(() => {
        if (!spaces)
            return [];
        const currentSpace = spaces?.find((space) => space.id === selectedSpaceId);
        if (currentSpace === undefined)
            return spaces;
        return [
            currentSpace,
            ...spaces.filter((item) => item.id !== currentSpace.id),
        ];
    }, [selectedSpaceId, spaces]);
    const showNotificationsWalkThrough = useAppSelector((store) => store.walkthrough.showNotificationsWalkThrough);
    const onLayout = useCallback((x, y, width, height) => onNotificationLayout({ x, y, width, height }), [reorderedSpaces, onNotificationLayout] // reorderedSpaces is necessary
    );
    const isWalkthroughActive = !isWeb && showNotificationsWalkThrough && !!notificationPosition;
    useLayoutEffect(() => {
        const onOpenNotifications = () => navigation.navigate('Notifications');
        navigation.setOptions({
            title: isWalkthroughActive ? '' : 'Notifications',
            headerTintColor: colors.text.primary,
            headerRight: () => (<NavigationHeaderRightIconButton disabled={isWalkthroughActive} onPress={onOpenNotifications} style={isWalkthroughActive ? { opacity: 0.2 } : undefined}>
          <IconSettings colorKey="primary"/>
        </NavigationHeaderRightIconButton>),
            headerBackVisible: !isWalkthroughActive,
            gestureEnabled: !isWalkthroughActive,
        });
    }, [colors.text.primary, isWalkthroughActive]);
    const shouldPreventBackButton = useCallback(() => isWalkthroughActive, [isWalkthroughActive]);
    useDisableAndroidBackButton(shouldPreventBackButton);
    const scrollX = useSharedValue(0);
    const scrollYs = useSharedValue({});
    const isUserScrolling = useSharedValue(false);
    const horizontalScrollViewRef = useAnimatedRef();
    const scrollY = useDerivedValue(() => {
        const page = Math.round(scrollX.value / screenWidth);
        const spaceId = reorderedSpaces[page]?.id;
        if (spaceId) {
            return scrollYs.value[spaceId] || 0;
        }
        return 0;
    });
    const scrollXHandler = useAnimatedScrollHandler({
        onBeginDrag: () => {
            isUserScrolling.value = true;
        },
        onScroll: ({ contentOffset }) => {
            scrollX.value = contentOffset.x;
        },
        onMomentumEnd: () => {
            if (isUserScrolling.value) {
                runOnJS(triggerSelectorPress)();
            }
            isUserScrolling.value = false;
            const currentPage = Math.round(scrollX.value / screenWidth);
            runOnJS(resetUnreadCount)(reorderedSpaces[currentPage].id);
            runOnJS(setPrevSpaceIndex)(currentPage);
        },
    });
    const scrollViewStyles = useMemo(() => ({
        width: reorderedSpaces.length * screenWidth,
    }), [reorderedSpaces.length, screenWidth]);
    const handleDismissWalkthrough = useCallback(() => {
        dispatch(setShowNotificationsWalkThrough(false));
        dispatch(setHasSeenNotificationsWalkThrough(true));
    }, []);
    return (<AppView withFrame={false}>
      <StatusBar barStyle="dark-content" animated/>
      <NotificationsListScreenHeader spaces={reorderedSpaces} scrollX={scrollX} scrollY={scrollY} horizontalScrollViewRef={horizontalScrollViewRef} displayCounts={displayCounts} resetUnreadCount={resetUnreadCount} setDisplayCounts={setDisplayCounts} prevSpaceIndex={prevSpaceIndex} setPrevSpaceIndex={setPrevSpaceIndex} isWalkthroughActive={isWalkthroughActive}/>
      <View style={styles.container}>
        <Animated.ScrollView horizontal pagingEnabled decelerationRate="fast" scrollEventThrottle={16} onScroll={scrollXHandler} ref={horizontalScrollViewRef} showsHorizontalScrollIndicator={false} contentContainerStyle={scrollViewStyles} scrollEnabled={!isWalkthroughActive}>
          {reorderedSpaces.map((space) => (<View key={space.id} style={[styles.container, { width: screenWidth }]} pointerEvents={isWalkthroughActive ? 'none' : 'auto'}>
              <NotificationContainer scrollYs={scrollYs} space={space} unreadCount={displayCounts[space.id] || 0} onFirstNotificationLayout={reorderedSpaces[prevSpaceIndex]?.id === space.id
                ? onLayout
                : undefined} scrollEnabled={!isWalkthroughActive}/>
            </View>))}
        </Animated.ScrollView>
      </View>
      {isWalkthroughActive ? (<NotificationsWalkthrough notificationPosition={notificationPosition} onDismiss={handleDismissWalkthrough}/>) : null}
    </AppView>);
};
const styles = StyleSheet.create({
    container: {
        flex: 1,
    },
});
